import { Add } from "@mui/icons-material";
import { Box, styled, Tab } from "@mui/material";
import { types } from "api";
import { ColorTabs } from "components/ColorTabs";
import { StyledButton } from "components/StyledButton";
import { Link, Redirect, useHistory, useRouteMatch } from "react-router-dom";
import { getPath, routes } from "routes";
import { ViewGroupsPageItem } from "./ViewGroupsPageItem";

const PREFIX = "ViewGroupsPage";

const classes = {
	root: `${PREFIX}-root`,
	tabs: `${PREFIX}-tabs`,
	tabsBarBox: `${PREFIX}-tabsBarBox`,
	button: `${PREFIX}-button`,
	buttonList: `${PREFIX}-buttonList`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		flexGrow: 1,
		backgroundColor: theme.palette.background.paper,
	},

	[`& .${classes.tabs}`]: {
		color: "white",
	},

	[`& .${classes.tabsBarBox}`]: {
		backgroundColor: theme.palette.primary.main,
	},

	[`& .${classes.button}`]: {
		minWidth: theme.spacing(21),
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},

	[`& .${classes.buttonList}`]: {
		display: "flex",
	},
}));

export interface ViewGroupsPageProps {
	groups: types.Group[];
	setGroups: (groups: types.Group[]) => void;
}

const getViewGroupPath = (groupId: string): string =>
	getPath(routes._.groups._.view, { groupId });

export const ViewGroupsPage = ({
	groups,
	setGroups,
}: ViewGroupsPageProps): JSX.Element => {
	const history = useHistory();

	const routeMatch = useRouteMatch<{ groupId: string }>(
		groups.map((group) => getViewGroupPath(group.groupId)),
	);

	if (routeMatch === null && groups.length > 0) {
		return <Redirect to={getViewGroupPath(groups[0]!.groupId)} />;
	}

	const updateGroup = (group: types.Group): void => {
		const index = groups.findIndex((item) => item.groupId === group.groupId);
		const groupCopy = [...groups];
		if (index !== -1) {
			groupCopy[index] = group;
			setGroups(groupCopy);
		}
	};

	// Used to remove a deleted group from the list of groups
	const deleteGroup = (group: types.Group): void => {
		const newGroups = groups.filter((item) => item.groupId !== group.groupId);
		setGroups(newGroups);
		history.push(getViewGroupPath(newGroups[0]?.groupId!));
	};

	return (
		<Root className={classes.root}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				className={classes.tabsBarBox}
			>
				<ColorTabs value={routeMatch?.path} variant="scrollable" scrollButtons>
					{groups.map((item: types.Group) => {
						const path = getViewGroupPath(item.groupId);

						return (
							<Tab
								key={item.groupId}
								id={item.groupId}
								label={item.name}
								value={path}
								data-testid={item.groupId}
								sx={{ color: "white" }}
								classes={{
									root: classes.tabs,
								}}
								component={Link}
								to={path}
							/>
						);
					})}
				</ColorTabs>

				<div className={classes.buttonList}>
					<StyledButton
						variant="contained"
						color="secondary"
						startIcon={<Add />}
						className={classes.button}
						onClick={() => history.push(routes._.groups._.create.path)}
						data-testid="create-group-btn"
					>
						Create Group
					</StyledButton>
				</div>
			</Box>

			<ViewGroupsPageItem
				groups={groups}
				updateGroup={updateGroup}
				deleteGroup={deleteGroup}
			/>
		</Root>
	);
};
